/**
 * COPIA DE CLIENTE de `supabase/functions/_shared/notificationRoute.ts`.
 *
 * Los dos lados no pueden compartir bundle —el edge function corre en Deno y no
 * puede importar de `src/`, y `tsconfig.app.json` solo incluye `src/`— así que
 * la tabla vive duplicada A PROPÓSITO, igual que `errorPolicy`. Quien la toque
 * tiene que tocar las dos: `notificationRoute.parity.test.ts` compara las dos
 * implementaciones caso por caso y falla si divergen.
 *
 * A dónde lleva un aviso. Una sola tabla, para la campana y para el push.
 *
 * POR QUÉ EXISTE (02-09-2026). El destino se calculaba en dos sitios: la
 * campana de dentro de la app (`resolveNotificationRoute`) y la función que
 * manda el push. Ya habían divergido: el push llevaba los partes de monitoreo a
 * `/pests?tab=scouting&session=…` y la campana no conocía ese caso siquiera, así
 * que el MISMO aviso abría dos sitios distintos según por dónde se tocara.
 *
 * Este módulo no importa nada —ni Deno ni navegador— para poder cargarse desde
 * la función edge y desde el test de paridad del cliente. Es el mismo apaño que
 * ya usa `errorPolicy.ts`, por el mismo motivo: los dos lados no pueden
 * compartir bundle, pero sí pueden compartir un fichero sin dependencias.
 *
 * EL ROL IMPORTA: un trabajador no tiene Planificador. Su tarea vive en
 * `/worker/tasks/:id`, y mandarle a `/planner` le deja en una pantalla que no
 * puede abrir. El perito tampoco (26-09-2026): su portal vive en `/perito/...`.
 *
 * CUATRO TIPOS SE FUERON (02-09-2026): `pest_alert` y `task_failed` no tenían
 * traducción y se leían como su clave cruda; `task_status_change` es imposible
 * de emitir desde que el disparador manda tipos concretos; y `scouting_created`
 * lo levantaba una función edge que ya no llama nadie. Ver la migración
 * `20260902220000_fuera_avisos_muertos.sql`.
 */

/** Avisos que hablan de una tarea del planificador. */
export const TIPOS_DE_TAREA = new Set([
  "task_created",
  "task_assigned",
  "task_updated",
  "task_started",
  "task_stopped",
  "task_completed",
  "task_cancelled",
  "task_due_soon",
  // Los avisos de una revisión pedida (D17): «no has empezado» y «te falta …».
  "followup_not_started",
  "followup_unfinished",
]);

/**
 * COPIA DE CLIENTE de `supabase/functions/_shared/notificationRoute.ts`.
 *
 * Los dos lados no pueden compartir bundle —el edge function corre en Deno y no
 * puede importar de `src/`, y `tsconfig.app.json` solo incluye `src/`— así que
 * la tabla vive duplicada A PROPÓSITO, igual que `errorPolicy`. Quien la toque
 * tiene que tocar las dos: `notificationRoute.parity.test.ts` compara las dos
 * implementaciones caso por caso y falla si divergen.
 *
 * Destino por tipo cuando el aviso no trae el id de la cosa concreta.
 *
 * Es peor que abrir la ficha exacta, pero mucho mejor que no ir a ninguna parte:
 * al menos deja al usuario en la pantalla donde está lo que se le avisa.
 */
export const RUTAS_POR_DEFECTO: Record<string, string> = {
  task_created: "/planner",
  task_assigned: "/planner",
  task_updated: "/planner",
  task_started: "/planner",
  task_stopped: "/planner",
  task_completed: "/planner",
  task_cancelled: "/planner",
  task_due_soon: "/planner",
  followup_not_started: "/worker/today",
  followup_unfinished: "/worker/today",
  worker_quick_report: "/pests?tab=scouting",
  low_stock: "/inventory",
  treatment_logged: "/spray-logs",
  irm_warning: "/pests?tab=treatments",
  product_request: "/inventory",
  // Quien pidió un producto se entera de que se lo atendieron o descartaron (M7).
  product_request_resolved: "/inventory",
  // Nace un foco grave (M17): hoy solo lo recibe el perito.
  pest_focus_new: "/pests?tab=scouting",
  pest_focus_worse: "/pests?tab=scouting",
  team_member_added: "/teams",
};

/**
 * Quién toca el aviso. Cada uno tiene SUS pantallas:
 *  - `worker`: la app del trabajador (`/worker/...`);
 *  - `perito`: su portal (`/perito/...`), que NO tiene `/planner`, `/pests` ni
 *    `/inventory`: mandarle ahí le devolvía al Inicio (auditoría A20/T04-02);
 *  - `other`: admin y encargado, con las rutas de siempre.
 */
export type RolDeAviso = "worker" | "perito" | "other";

export interface EntradaDeAviso {
  type?: string | null;
  data?: Record<string, unknown> | null;
  /** El trabajador tiene sus propias pantallas. Se sigue admitiendo; `rol` manda. */
  isWorker?: boolean;
  rol?: RolDeAviso;
}

/** Visitas e inspecciones viven en Seguimiento en el portal del perito. */
const TIPOS_DE_SEGUIMIENTO = new Set(["visit", "inspection"]);

function rutaDePerito(t: string, d: Record<string, unknown>): string | undefined {
  if (TIPOS_DE_TAREA.has(t)) {
    const base = TIPOS_DE_SEGUIMIENTO.has(String(d.task_type ?? ""))
      ? "/perito/seguimiento"
      : "/perito/planificador";
    return d.task_id ? `${base}?taskId=${d.task_id}` : base;
  }
  switch (t) {
    case "worker_quick_report":
    case "pest_focus_new":
    case "pest_focus_worse":
      return d.session_id
        ? `/perito/sanidad?tab=scouting&session=${d.session_id}`
        : "/perito/sanidad?tab=scouting";
    case "irm_warning":
      return "/perito/sanidad";
    case "low_stock":
      return d.inventory_id ? `/perito/inventario/${d.inventory_id}` : "/perito/inventario";
    case "product_request":
    case "product_request_resolved":
      return "/perito/inventario?solicitudes=1";
    case "treatment_logged":
      return d.task_id ? `/perito/planificador?taskId=${d.task_id}` : "/perito/planificador";
    case "team_member_added":
      return undefined;
    default:
      return typeof d.route === "string" ? d.route : undefined;
  }
}

/**
 * La ruta a la que lleva este aviso, o `undefined` si no lleva a ninguna parte.
 *
 * `data.route` sigue admitiéndose como comodín para que quien manda un aviso a
 * medida pueda decir el destino a mano; manda menos que las reglas de arriba
 * porque esas conocen el rol.
 *
 * Una tarea BORRADA (`data.deleted`, M20) no lleva a ninguna parte: la ficha ya
 * no existe y abrir el Planificador vacío despista más que quedarse donde está.
 */
export function rutaDeAviso({ type, data, isWorker = false, rol }: EntradaDeAviso): string | undefined {
  const t = type || "";
  const d = data ?? {};
  const quien: RolDeAviso = rol ?? (isWorker ? "worker" : "other");

  if (TIPOS_DE_TAREA.has(t) && d.deleted) return undefined;
  if (quien === "perito") return rutaDePerito(t, d);

  const worker = quien === "worker";
  const aMano = typeof d.route === "string" ? d.route : undefined;
  const porDefecto = aMano ?? RUTAS_POR_DEFECTO[t];

  if (TIPOS_DE_TAREA.has(t)) {
    if (!d.task_id) return worker ? "/worker/today" : porDefecto;
    return worker ? `/worker/tasks/${d.task_id}` : `/planner?taskId=${d.task_id}`;
  }
  if (t === "treatment_logged" && d.spray_log_id) return `/spray-logs?logId=${d.spray_log_id}`;
  if (t === "low_stock" && d.inventory_id) return `/inventory/${d.inventory_id}?action=entry`;
  if (t === "product_request" && d.request_id) return `/inventory?requestId=${d.request_id}`;
  if ((t === "worker_quick_report" || t === "pest_focus_new" || t === "pest_focus_worse") && d.session_id) {
    return `/pests?tab=scouting&session=${d.session_id}`;
  }
  return porDefecto;
}

/* ────────────────────────────────────────────────────────────────────────────
 * A PARTIR DE AQUÍ, SOLO CLIENTE. Nada de esto existe en la copia del edge
 * function y por eso no entra en el test de paridad: el servidor CONSTRUYE el
 * aviso, y esto es para LEER lo que el envoltorio nativo devuelve al tocarlo.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Los campos del aviso, venga como venga envuelto.
 *
 * POR QUÉ NO BASTA CON MIRAR `payload.data` (02-09-2026). El usuario probó el
 * arranque en frío con el arreglo puesto: el Planificador ya cargaba, pero la
 * ficha de la tarea NO se abría. En la base de datos el aviso llevaba su
 * `task_id`, y `rutaDeAviso` calcula bien `/planner?taskId=…` — o sea que el id
 * se perdía en el camino entre el servidor y la app.
 *
 * Los dos sospechosos son los mismos de siempre y no se pueden distinguir desde
 * aquí: o la función edge desplegada es anterior a `path` (y entonces solo llega
 * el `route: "/planner"` pelado que escribe el disparador), o el envoltorio
 * entrega el `data` de OneSignal anidado donde no lo estábamos buscando —
 * `additionalData`, `notification.additionalData`, `custom.a`… cada SDK lo pone
 * en un sitio.
 *
 * Así que se dejan de adivinar las dos cosas: se buscan las claves que nos
 * importan POR TODO el objeto, hasta cuatro niveles, y lo de más adentro manda
 * (es lo más específico). Solo se copian estas claves, no el objeto entero: un
 * `type` de OneSignal que signifique otra cosa no puede colarse por el camino.
 */
const CLAVES_DE_AVISO = [
  "path",
  "route",
  "type",
  "task_id",
  /* El perito abre las visitas en Seguimiento y el resto en su Planificador,
     y una tarea borrada no lleva a ninguna parte (26-09-2026). */
  "task_type",
  "deleted",
  "session_id",
  "spray_log_id",
  "inventory_id",
  "request_id",
] as const;

export function camposDelAviso(payload: unknown, profundidad = 4): Record<string, unknown> {
  const salida: Record<string, unknown> = {};
  const visitar = (valor: unknown, quedan: number) => {
    if (quedan < 0 || !valor || typeof valor !== "object" || Array.isArray(valor)) return;
    const objeto = valor as Record<string, unknown>;
    for (const clave of CLAVES_DE_AVISO) {
      const v = objeto[clave];
      /* Se escribe ANTES de bajar, así que lo de dentro pisa a lo de fuera. */
      if (v !== undefined && v !== null && v !== "") salida[clave] = v;
    }
    for (const hijo of Object.values(objeto)) visitar(hijo, quedan - 1);
  };
  visitar(payload, profundidad);
  return salida;
}

/**
 * Del rol de la sesión (`useAuth().role`) al rol que entiende `rutaDeAviso`.
 * Solo cliente: la función del push lo deduce de `user_roles`.
 */
export function rolDeAviso(role: string | null | undefined): RolDeAviso {
  if (role === "worker") return "worker";
  if (role === "perito") return "perito";
  return "other";
}
